import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { founderImageUrl, homeResultsPreview, systems } from "@/components/equinox/data";
import { TiltCard } from "@/components/equinox/MotionEnhancers";

export default function SystemsAndProofSection({ onOpenChatbot }) {
  const { scrollY } = useScroll();
  const railX = useTransform(scrollY, [180, 980], [0, -180]);

  return (
    <div className="px-6 sm:px-8 lg:px-12">
      <section id="systems" className="mx-auto max-w-7xl py-24 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 max-w-4xl"
        >
          <p className="section-kicker">Systems</p>
          <h2 className="section-heading mt-5 text-white">Six growth systems built to move installs, CAC, and ratings.</h2>
          <p className="section-copy mt-6 text-white/[0.62]">
            Each layer is designed as part of one operating architecture — not fragmented activity. That’s what makes the system feel premium, measurable, and scalable.
          </p>
        </motion.div>

        <div className="relative mt-12 grid gap-6 md:grid-cols-8 xl:grid-cols-12" data-testid="systems-horizontal-rail">
          <motion.div style={{ x: railX }} className="contents">
          {systems.map(({ icon: Icon, title, description, glow, testId, slug }, index) => (
            <Link key={title} to={`/services/${slug}`} className="contents" data-testid={`home-systems-card-link-${index + 1}`}>
              <TiltCard
                className={`group premium-border glass-panel glitter-card cinematic-card relative min-h-[250px] overflow-hidden rounded-[2rem] p-6 transition-transform duration-500 hover:-translate-y-1.5 ${index === 0 || index === 5 ? "md:col-span-4 xl:col-span-4" : index === 1 || index === 4 ? "md:col-span-4 xl:col-span-5" : "md:col-span-4 xl:col-span-3"}`}
                testId={testId}
              >
                <motion.div
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.55, delay: index * 0.08 }}
                  className="flex h-full flex-col"
                >
                    <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${glow} blur-2xl transition-opacity duration-500 group-hover:opacity-100`} />
                  <div className="relative flex h-full flex-col">
                    <div className="icon-drift mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.06] text-blue-100 shadow-[0_0_30px_rgba(37,99,235,0.12)]">
                    <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-display text-[1.4rem] text-white">{title}</h3>
                    <p className="mt-4 text-sm leading-8 text-white/[0.65]">{description}</p>
                    <div className="mt-5 inline-flex rounded-full border border-white/[0.12] bg-white/[0.05] px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-blue-100">
                      {systems[index].outcome}
                    </div>
                    <div className="mt-auto pt-6 text-sm font-medium text-white/84" data-testid={`home-systems-card-cta-${index + 1}`}>
                      View full service details →
                    </div>
                  </div>
                </motion.div>
              </TiltCard>
            </Link>
          ))}
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl py-24 lg:py-32" data-testid="client-results-preview-section">
        <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="section-kicker">Client Results Preview</p>
            <h2 className="section-heading mt-5 text-white">Proof that focuses on installs, CAC, and quality signals.</h2>
          </div>
          <Link
            to="/case-studies"
            className="glass-button glass-panel rounded-full px-6 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
            data-testid="client-results-preview-link"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              See Case Studies
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {homeResultsPreview.map((item, index) => (
            <TiltCard key={item.title} className="editorial-shell glitter-card glass-panel cinematic-card rounded-[2rem] p-7" testId={`results-preview-card-${index + 1}`}>
              <p className="text-xs uppercase tracking-[0.22em] text-white/[0.42]">{item.title}</p>
              <p className="mt-5 font-display text-4xl text-white">{item.metric}</p>
              <p className="mt-5 text-sm leading-8 text-white/[0.65]">{item.detail}</p>
            </TiltCard>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl py-24 lg:py-32" data-testid="home-cta-section">
        <div className="glitter-card glass-panel rounded-[2.4rem] p-6 lg:p-8">
          <div className="grid gap-8 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div className="overflow-hidden rounded-[1.8rem] border border-white/[0.08] bg-black/20 p-3">
              <img src={founderImageUrl} alt="Shivam Ray" className="aspect-[4/5] w-full rounded-[1.4rem] object-cover grayscale" data-testid="home-founder-image" />
            </div>
            <div>
              <p className="section-kicker">About Founder</p>
              <h2 className="mt-5 max-w-[15ch] font-display text-3xl tracking-[-0.04em] text-white sm:text-4xl">Shivam Ray builds growth systems, not disconnected marketing activity.</h2>
              <p className="section-copy mt-5 max-w-2xl text-base text-white/[0.66]">
                With hands-on experience across app growth, paid acquisition, ratings, and automation, Shivam leads Equinox like an execution-first growth partner for serious brands.
              </p>
              <p className="mt-4 text-sm uppercase tracking-[0.24em] text-blue-100">Free audit • No commitment • 15-min call</p>
              <button
                type="button"
                onClick={onOpenChatbot}
                className="glass-button glass-panel mt-8 rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03]"
                data-testid="home-strong-cta-button"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  Book a Growth Call
                  <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}